/**
 * useUnsavedChanges — dirty-state protection (Phase 2.4 TZ 2026-08-07).
 *
 * Два канала защиты несохранённых правок:
 *  1. **browser beforeunload** — нативный prompt при попытке закрыть вкладку /
 *     перезагрузить страницу / уйти на external URL.
 *  2. **react-router route blocker** — `useBlocker` ловит
 *     программную навигацию внутри SPA. При dirty показываем confirm() и
 *     блокируем переход, если пользователь отказался.
 *
 * i18n ключи не хардкодим здесь — caller передаёт message через `confirmMessage`.
 *
 * @param isDirty — true, когда local state отличается от server state.
 * @param confirmMessage — текст подтверждения.
 * @returns blocker (router) + beforeunload handler (window).
 */
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

export function useUnsavedChanges(isDirty: boolean, confirmMessage: string) {
  // browser beforeunload — нативный prompt.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Браузеры требуют returnValue для показа prompt'а.
      e.returnValue = confirmMessage;
      return confirmMessage;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, confirmMessage]);

  // SPA route blocker — ловит <Link>/navigate() внутри приложения.
  const blocker = useBlocker(isDirty);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const ok = window.confirm(confirmMessage);
    if (ok) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, confirmMessage]);
}
